import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { differenceInDays } from 'date-fns';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import {
  CalendarEventActionDetailsFragment, CalendarEventActionPurposeEnum,
  CreateActionForCalendarEventActionGQL, UpdateCalendarEventActionScheduledDaysGQL
} from 'generated/graphql';

import {
  getTaskDefaultDaysBefore, getTaskIcon, getTaskLabel
} from 'app/events/helpers/event-task-helpers';
import { ToastrService } from 'core/toastr.service';
import { validationToErrorMessages } from 'core/validators';

const validationMessages = {
  scheduleCreateDaysBeforeEventStart: {
    required: 'Enter number of days before the event to schedule the task.',
    min: 'Days before event must be at least 0.',
    max: 'Scheduled days results in a past date.',
  },
  attendanceDaysAfterEventStart: {
    required: 'Enter number of days after the event to schedule the task.',
    min: 'Days after event must be at least 0.',
  },
};

@Component({
  selector: 'app-add-task-dialog',
  templateUrl: `./add-task.dialog.html`,
})
export class AddTaskDialog implements OnInit {
  static open(dialog: MatDialog, config: MatDialogConfig<any>) {
    return dialog.open(AddTaskDialog, config);
  }

  getTaskDisplayLabel = getTaskLabel;
  getTaskIconClass = getTaskIcon;
  attendanceTaskPurpose = CalendarEventActionPurposeEnum.LogAttendance;
  task: CalendarEventActionDetailsFragment;
  isSubmitting = false;
  taskTypeFC = new FormControl<string>('schedule');
  scheduleCreateDaysBeforeEventStartFC = new FormControl<number>(null);
  attendanceDaysAfterEventStartFC = new FormControl<number>(0);

  taskForm = new FormGroup({
    taskType: this.taskTypeFC,
    scheduleCreateDaysBeforeEventStart: this.scheduleCreateDaysBeforeEventStartFC,
    attendanceDaysAfterEventStart: this.attendanceDaysAfterEventStartFC,
  });

  daysMax = 100; // Updated when task is loaded

  constructor(
    private toastrService: ToastrService,
    private _cd: ChangeDetectorRef,
    private _createActionForCalendarEventActionGQL: CreateActionForCalendarEventActionGQL,
    private _updateCalendarEventActionScheduledDaysGQL: UpdateCalendarEventActionScheduledDaysGQL,
    public _dialogRef: MatDialogRef<AddTaskDialog>,
    @Inject(MAT_DIALOG_DATA) public data: { task: CalendarEventActionDetailsFragment }
  ) {
    if (data && data.task) {
      this.task = data.task;
      const eventStart = new Date(this.task.calendarEvent.startDate);
      // Maximum days before the event that we can schedule a task
      this.daysMax = Math.max(
        // Shouldn't schedule before today
        differenceInDays(eventStart, new Date()),
        // Minimum 0
        0,
        // Below can probably be removed?
        // // Allow default task days before
        // getTaskDefaultDaysBefore(this.task.actionPurpose),
        // // Allow current value
        // this.task.scheduleCreateDaysBeforeEventStart,
      );
      // Default days before
      this.taskForm.patchValue({
        scheduleCreateDaysBeforeEventStart:
          data.task.scheduleCreateDaysBeforeEventStart === null ?
            getTaskDefaultDaysBefore(data.task.actionPurpose)
            : data.task.scheduleCreateDaysBeforeEventStart,
        attendanceDaysAfterEventStart:
          // For attendance, use days after event start
          data.task.actionPurpose === this.attendanceTaskPurpose &&
            data.task.scheduleCreateDaysBeforeEventStart !== null ?
            data.task.scheduleCreateDaysBeforeEventStart * -1 :
            getTaskDefaultDaysBefore(data.task.actionPurpose),
      });
    }
  }

  ngOnInit(): void {
    this.taskTypeFC.valueChanges.subscribe(taskType => {
      this.updateValidators(taskType);
    });

    // Initialize the validators based on the initial value
    this.updateValidators(this.taskTypeFC.value);
  }

  updateValidators(taskType: string) {
    if (taskType === 'schedule') {
      this.scheduleCreateDaysBeforeEventStartFC.setValidators([
        Validators.required,
        Validators.min(0),
        (control) => {
          const days = control.value;
          if (days < 0) {
            return { min: true };
          }
          if (days > this.daysMax) {
            return { max: true };
          }
          return null;
        }
      ]);
    } else {
      this.scheduleCreateDaysBeforeEventStartFC.clearValidators();
    }
    // Re-validate the form control
    this.scheduleCreateDaysBeforeEventStartFC.updateValueAndValidity();
  }


  getErrorMessages() {
    const messages = validationToErrorMessages(
      this.taskForm,
      validationMessages
    );

    return messages;
  }

  // No-op for now. This was breaking validations
  validate() {
    // this.errorMessages = validationToErrorMessages(
    //       this.taskForm,
    //       validationMessages
    // );
  }

  close() {
    this._dialogRef.close();
  }

  updateTask() {
    if (!this.taskForm.valid) {
      this.isSubmitting = false;
      this._cd.markForCheck();
      return;
    }
    this.isSubmitting = true;

    this._runMutation()
      .pipe(
        finalize(() => this.isSubmitting = false),
      )
      .subscribe({
        next: (message) => {
          this.toastrService.success(message);
          this.close();
        },
        error: (e) => {
          this.toastrService.error(e.message);
        },
      });
  }

  private _runMutation(): Observable<string> {
    if (this.taskForm.value.taskType === 'schedule') {
      return this._scheduleTask$().pipe(
        map((cea) => `The ${this.getTaskDisplayLabel(cea.actionPurpose)}task is scheduled.`),
      );
    }

    if (this.taskForm.value.taskType === 'cancel') {
      return this._cancelTask$().pipe(
        map((cea) => `The ${this.getTaskDisplayLabel(cea.actionPurpose)} task is no longer scheduled.`),
      );
    }

    return this._createTask$().pipe(
      map((cea) => `The ${this.getTaskDisplayLabel(cea.actionPurpose)} task was created.`),
    );
  }

  private _cancelTask$() {
    return this._updateCalendarEventActionScheduledDaysGQL
      .mutate({
        input: {
          calendarEventActionId: this.task.id,
          scheduledCreateDaysOut: null,
        },
      }).pipe(
        map(
          ({ data }) => data.updateCalendarEventActionScheduledDays.calendarEventAction
        )
      );
  }

  private _scheduleTask$() {
    return this._updateCalendarEventActionScheduledDaysGQL
      .mutate({
        input: {
          calendarEventActionId: this.task.id,
          scheduledCreateDaysOut:
            // For attendance, use days after event start
            this.task.actionPurpose === this.attendanceTaskPurpose
              ? this.taskForm.value.attendanceDaysAfterEventStart * -1
              : this.taskForm.value.scheduleCreateDaysBeforeEventStart,
        },
      }).pipe(
        map(
          ({ data }) => data.updateCalendarEventActionScheduledDays.calendarEventAction
        )
      );
  }

  private _createTask$() {
    return this._createActionForCalendarEventActionGQL
      .mutate({
        input: {
          id: this.task.id,
        }
      }).pipe(
        map(
          ({ data }) => data.createActionForCalendarEventAction.calendarEventAction
        )
      );
  }
}
